import { useEffect, useState, useCallback } from "react";
import { api } from "@/lib/api";
import { toast } from "sonner";
import { X, Mail, Loader2, Search, RefreshCw, DownloadCloud, Send, Sparkles, Reply, PenSquare, FileText, ListChecks, CreditCard, ChevronLeft, Globe } from "lucide-react";

const CATS = [
  { key: "", label: "All" }, { key: "primary", label: "Primary" }, { key: "promotions", label: "Promotions" },
  { key: "social", label: "Social" }, { key: "updates", label: "Updates" }, { key: "forums", label: "Forums" }, { key: "sent", label: "Sent" },
];
const AI_ACTIONS = [
  { key: "draft_reply", label: "Draft reply", icon: Reply },
  { key: "summarize", label: "Summarize", icon: FileText },
  { key: "extract_actions", label: "Action items", icon: ListChecks },
  { key: "flag_credit", label: "Credit?", icon: CreditCard },
];
const fmt = (iso) => { try { return new Date(iso).toLocaleString([], { month: "short", day: "numeric", hour: "2-digit", minute: "2-digit" }); } catch { return ""; } };

export default function EmailClient({ onClose }) {
  const [cat, setCat] = useState("");
  const [q, setQ] = useState("");
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(true);
  const [page, setPage] = useState(0);
  const [open, setOpen] = useState(null);
  const [aiBusy, setAiBusy] = useState(null);
  const [aiResult, setAiResult] = useState(null);
  const [aiPrompt, setAiPrompt] = useState("");
  const [compose, setCompose] = useState(null);
  const [sending, setSending] = useState(false);
  const [sync, setSync] = useState(null);
  const [searching, setSearching] = useState(false);
  const [live, setLive] = useState(false);

  const load = useCallback(() => {
    setLoading(true);
    api.gmailMessages(cat, q, page, 30).then(setData).catch(() => setData({ messages: [], total: 0 })).finally(() => setLoading(false));
  }, [cat, q, page]);
  useEffect(() => { if (!live) load(); }, [load, live]);

  useEffect(() => {
    if (sync?.sync_status !== "running") return;
    const t = setInterval(() => api.gmailClientStatus().then((s) => { setSync(s); if (s.sync_status === "done") { toast.success(`Loaded ${s.synced} emails`); load(); } }), 2500);
    return () => clearInterval(t);
  }, [sync?.sync_status, load]);

  const runSync = async () => {
    const categories = cat ? [cat === "sent" ? "primary" : cat] : ["primary", "updates"];
    try { const r = await api.gmailClientSync(categories, 150); toast.success(r.message); const s = await api.gmailClientStatus(); setSync(s); }
    catch (e) { toast.error(e.response?.data?.detail || "Could not load"); }
  };

  const openMsg = async (m) => {
    setAiResult(null); setAiPrompt("");
    if (m._live) { setOpen(m); return; }
    try { const full = await api.gmailMessage(m.id); setOpen(full); } catch { toast.error("Could not open"); }
  };

  const searchAll = async () => {
    if (!q.trim()) { toast.error("Type what to search for across all mail"); return; }
    setSearching(true); setOpen(null); setLoading(true);
    try {
      const r = await api.gmailSearchAll(q.trim(), 40);
      const messages = (r.results || []).map((x, i) => ({
        id: `live-${i}`, _live: true, from_name: x.from, from_addr: x.from_addr,
        subject: x.subject, occurred_at: x.date, snippet: x.snippet, body: x.snippet, unread: false, direction: "inbox",
      }));
      setData({ messages, total: r.count || messages.length, live: true });
      setLive(true);
      if (!messages.length) toast.info("No matching emails found in your full history");
    } catch (e) { toast.error(e.response?.data?.detail || "Search failed"); }
    finally { setSearching(false); setLoading(false); }
  };

  const exitLive = () => { setLive(false); setOpen(null); setQ(""); setPage(0); };

  const runAi = async (action, prompt = "") => {
    if (!open) return;
    setAiBusy(action || "freeform"); setAiResult(null);
    try {
      const r = await api.gmailAiAction(open.id, action, prompt);
      setAiResult({ action: action || "freeform", text: r.result });
    } catch (e) { toast.error(e.response?.data?.detail || "AI failed"); }
    finally { setAiBusy(null); }
  };

  const startReply = (prefill = "") => {
    if (!open) return;
    setCompose({ to: open.from_addr, subject: open.subject?.startsWith("Re:") ? open.subject : `Re: ${open.subject || ""}`, body: prefill, reply_to: open.message_id });
  };

  const doSend = async () => {
    if (!compose?.to || !compose?.body?.trim()) { toast.error("Add a recipient and a message"); return; }
    setSending(true);
    try { const r = await api.gmailSend(compose.to, compose.subject, compose.body, compose.reply_to || ""); toast.success(r.message); setCompose(null); if (cat === "sent") load(); }
    catch (e) { toast.error(e.response?.data?.detail || "Could not send"); }
    finally { setSending(false); }
  };

  const msgs = data?.messages || [];
  const running = sync?.sync_status === "running";

  return (
    <div className="h-full flex flex-col">
      <div className="flex items-center justify-between px-4 pt-4 pb-3 border-b border-white/10">
        <h2 className="font-display uppercase tracking-widest text-cyan-300 text-lg flex items-center gap-2"><Mail size={18} /> Email</h2>
        <div className="flex items-center gap-2">
          <button data-testid="email-compose-button" onClick={() => setCompose({ to: "", subject: "", body: "", reply_to: "" })} className="flex items-center gap-1.5 text-xs font-tactical font-bold uppercase bg-cyan-500 hover:bg-cyan-400 text-[#04070c] rounded-lg px-3 py-1.5"><PenSquare size={14} /> Compose</button>
          <button data-testid="email-close-button" onClick={onClose} className="w-8 h-8 grid place-items-center rounded-lg bg-white/5 hover:bg-white/10 text-zinc-400"><X size={16} /></button>
        </div>
      </div>

      {/* Category chips + search + sync */}
      <div className="px-4 py-2.5 border-b border-white/10 flex items-center gap-2 flex-wrap">
        {CATS.map((c) => (
          <button key={c.key || "all"} data-testid={`email-cat-${c.key || "all"}`} onClick={() => { setLive(false); setCat(c.key); setPage(0); setOpen(null); }} className={`text-[11px] font-tactical uppercase tracking-wider px-2.5 py-1 rounded-lg border transition-colors ${!live && cat === c.key ? "border-cyan-400/50 bg-cyan-500/15 text-cyan-200" : "border-white/10 text-zinc-500 hover:text-zinc-300"}`}>{c.label}</button>
        ))}
        <div className="flex-1 min-w-[160px] relative">
          <Search size={13} className="absolute left-2.5 top-1/2 -translate-y-1/2 text-zinc-500" />
          <input data-testid="email-search-input" value={q} onChange={(e) => setQ(e.target.value)} onKeyDown={(e) => { if (e.key === "Enter") { if (e.shiftKey || live) { searchAll(); } else { setPage(0); load(); } } }} placeholder="Search — Enter for this view, or use All-mail →" className="w-full bg-[#0b0e14] border border-white/10 rounded-lg pl-8 pr-3 py-1.5 text-sm text-zinc-100 focus:outline-none focus:ring-2 focus:ring-cyan-400/40" />
        </div>
        <button data-testid="email-search-all-button" onClick={searchAll} disabled={searching} title="Live-search your entire Gmail history (not just what's loaded)" className={`flex items-center gap-1.5 text-[11px] font-tactical font-bold uppercase rounded-lg px-3 py-1.5 border transition-colors ${live ? "bg-violet-500/20 border-violet-400/50 text-violet-100" : "bg-violet-500/10 hover:bg-violet-500/20 border-violet-400/30 text-violet-200"}`}>{searching ? <Loader2 size={13} className="animate-spin" /> : <Globe size={13} />} All mail</button>
        <button data-testid="email-sync-button" onClick={runSync} disabled={running} className="flex items-center gap-1.5 text-[11px] font-tactical font-bold uppercase bg-white/10 hover:bg-white/20 text-zinc-100 rounded-lg px-3 py-1.5">{running ? <Loader2 size={13} className="animate-spin" /> : <DownloadCloud size={13} />}{running ? `Loading ${sync.synced || 0}` : "Load from Gmail"}</button>
      </div>

      {live && (
        <div className="px-4 py-1.5 bg-violet-500/[0.07] border-b border-violet-500/20 flex items-center justify-between text-[11px] font-mono text-violet-200">
          <span className="flex items-center gap-1.5"><Globe size={12} /> Live results from your entire Gmail for “{q}” · {data?.total || 0} found</span>
          <button data-testid="email-exit-live" onClick={exitLive} className="text-zinc-400 hover:text-zinc-200">← Back to folders</button>
        </div>
      )}

      <div className="flex-1 min-h-0 flex">
        {/* List */}
        <div className={`${open ? "hidden md:block md:w-[38%]" : "w-full"} border-r border-white/10 overflow-y-auto thin-scroll`} data-testid="email-list">
          {loading ? (
            <div className="flex items-center gap-2 text-zinc-400 text-sm font-mono p-6 justify-center"><Loader2 size={15} className="animate-spin" /> Loading…</div>
          ) : msgs.length === 0 ? (
            <div className="text-center text-zinc-500 text-sm p-8">{live ? "No emails matched that search in your full history. Try different words." : <>No emails here yet. Hit <strong>Load from Gmail</strong> to pull this category (free), or use <strong>All mail</strong> to live-search everything.</>}</div>
          ) : msgs.map((m) => (
            <button key={m.id} data-testid={`email-row-${m.id}`} onClick={() => openMsg(m)} className={`w-full text-left px-4 py-3 border-b border-white/5 hover:bg-white/[0.03] ${open?.id === m.id ? "bg-cyan-500/[0.06]" : ""}`}>
              <div className="flex items-center justify-between gap-2">
                <span className={`text-sm truncate ${m.unread ? "text-zinc-100 font-semibold" : "text-zinc-300"}`}>{m.direction === "sent" ? `To: ${m.to_addr}` : m.from_name}</span>
                <span className="text-[10px] font-mono text-zinc-500 shrink-0">{fmt(m.occurred_at)}</span>
              </div>
              <div className="text-[13px] text-zinc-300 truncate mt-0.5">{m.subject || "(no subject)"}</div>
              <div className="text-[11px] text-zinc-500 truncate">{m.snippet}</div>
            </button>
          ))}
          {!loading && !data?.live && (data?.total || 0) > 30 && (
            <div className="flex items-center justify-between p-3 text-[11px] font-mono text-zinc-400">
              <button disabled={page === 0} onClick={() => setPage((p) => Math.max(0, p - 1))} className="disabled:opacity-30 hover:text-cyan-300">← Prev</button>
              <span>Page {page + 1} · {data.total} total</span>
              <button disabled={(page + 1) * 30 >= data.total} onClick={() => setPage((p) => p + 1)} className="disabled:opacity-30 hover:text-cyan-300">Next →</button>
            </div>
          )}
        </div>

        {/* Reader */}
        {open && (
          <div className="flex-1 min-w-0 overflow-y-auto thin-scroll" data-testid="email-reader">
            <div className="p-4">
              <button onClick={() => setOpen(null)} className="md:hidden flex items-center gap-1 text-xs text-zinc-400 mb-2"><ChevronLeft size={14} /> Back</button>
              <div className="text-lg font-tactical font-bold text-zinc-100">{open.subject || "(no subject)"}</div>
              <div className="text-[12px] text-zinc-400 mt-1">{open.direction === "sent" ? `To ${open.to_addr}` : `${open.from_name} · ${open.from_addr}`} · {fmt(open.occurred_at)}</div>
              {open._live ? (
                <div className="mt-3 flex items-center gap-2 flex-wrap">
                  <button data-testid="email-reply-button" onClick={() => startReply()} className="flex items-center gap-1 text-[11px] font-tactical font-bold uppercase text-cyan-200 border border-cyan-400/30 rounded-lg px-2.5 py-1 hover:bg-cyan-500/15"><Reply size={12} /> Reply</button>
                  <span className="text-[11px] font-mono text-violet-300/80 flex items-center gap-1"><Globe size={11} /> Live preview · Load from Gmail for full body & AI actions</span>
                </div>
              ) : (<>
              <div className="flex items-center gap-2 mt-3 flex-wrap">
                <button data-testid="email-reply-button" onClick={() => startReply()} className="flex items-center gap-1 text-[11px] font-tactical font-bold uppercase text-cyan-200 border border-cyan-400/30 rounded-lg px-2.5 py-1 hover:bg-cyan-500/15"><Reply size={12} /> Reply</button>
                {AI_ACTIONS.map((a) => (
                  <button key={a.key} data-testid={`email-ai-${a.key}`} onClick={() => runAi(a.key)} disabled={aiBusy} className="flex items-center gap-1 text-[11px] font-mono text-violet-200 border border-violet-400/30 rounded-lg px-2.5 py-1 hover:bg-violet-500/15 disabled:opacity-40">{aiBusy === a.key ? <Loader2 size={12} className="animate-spin" /> : <a.icon size={12} />} {a.label}</button>
                ))}
              </div>

              <div className="mt-3 flex items-end gap-2">
                <input data-testid="email-ai-prompt" value={aiPrompt} onChange={(e) => setAiPrompt(e.target.value)} onKeyDown={(e) => { if (e.key === "Enter" && aiPrompt.trim()) runAi("freeform", aiPrompt.trim()); }} placeholder="Tell AI what to do with this email…" className="flex-1 bg-[#0b0e14] border border-white/10 rounded-lg px-3 py-2 text-sm text-zinc-100 focus:outline-none focus:ring-2 focus:ring-violet-400/40" />
                <button data-testid="email-ai-run" onClick={() => aiPrompt.trim() && runAi("freeform", aiPrompt.trim())} disabled={aiBusy || !aiPrompt.trim()} className="h-9 px-3 rounded-lg bg-violet-500 hover:bg-violet-400 text-[#04070c] font-tactical font-bold uppercase text-xs flex items-center gap-1">{aiBusy === "freeform" ? <Loader2 size={13} className="animate-spin" /> : <Sparkles size={13} />} Ask</button>
              </div>

              {aiResult && (
                <div data-testid="email-ai-result" className="mt-3 rounded-lg bg-violet-500/[0.06] border border-violet-500/20 p-3">
                  <div className="flex items-center justify-between mb-1">
                    <span className="text-[10px] uppercase tracking-wider text-violet-300 font-mono flex items-center gap-1"><Sparkles size={11} /> AI · {aiResult.action.replace("_", " ")}</span>
                    {aiResult.action === "draft_reply" && <button onClick={() => startReply(aiResult.text)} className="text-[10px] font-mono text-cyan-300 hover:underline">Use as reply →</button>}
                  </div>
                  <pre className="text-[13px] text-zinc-100 whitespace-pre-wrap font-sans leading-relaxed">{aiResult.text}</pre>
                </div>
              )}
              </>)}

              <div className="mt-4 pt-4 border-t border-white/10 text-[14px] text-zinc-200 whitespace-pre-wrap leading-relaxed">{open.body || "(empty)"}</div>
            </div>
          </div>
        )}
      </div>

      {/* Compose modal */}
      {compose && (
        <div className="fixed inset-0 z-50 grid place-items-center bg-black/60 backdrop-blur-sm p-4" onClick={() => setCompose(null)}>
          <div data-testid="email-compose-modal" onClick={(e) => e.stopPropagation()} className="w-full max-w-lg rounded-2xl border border-white/10 bg-[#0b0e14] p-5 anim-slidein">
            <div className="flex items-center justify-between mb-3">
              <h3 className="font-tactical font-bold uppercase text-sm text-cyan-300">{compose.reply_to ? "Reply" : "New email"}</h3>
              <button onClick={() => setCompose(null)} className="text-zinc-400 hover:text-zinc-200"><X size={16} /></button>
            </div>
            <input data-testid="compose-to" value={compose.to} onChange={(e) => setCompose({ ...compose, to: e.target.value })} placeholder="To" className="w-full bg-[#05070b] border border-white/10 rounded-lg px-3 py-2 text-sm text-zinc-100 mb-2 focus:outline-none focus:ring-2 focus:ring-cyan-400/40" />
            <input data-testid="compose-subject" value={compose.subject} onChange={(e) => setCompose({ ...compose, subject: e.target.value })} placeholder="Subject" className="w-full bg-[#05070b] border border-white/10 rounded-lg px-3 py-2 text-sm text-zinc-100 mb-2 focus:outline-none focus:ring-2 focus:ring-cyan-400/40" />
            <textarea data-testid="compose-body" rows={8} value={compose.body} onChange={(e) => setCompose({ ...compose, body: e.target.value })} placeholder="Write your message…" className="w-full bg-[#05070b] border border-white/10 rounded-lg px-3 py-2 text-sm text-zinc-100 resize-none focus:outline-none focus:ring-2 focus:ring-cyan-400/40" />
            <div className="flex justify-end mt-3">
              <button data-testid="compose-send" onClick={doSend} disabled={sending} className="flex items-center gap-1.5 text-xs font-tactical font-bold uppercase bg-cyan-500 hover:bg-cyan-400 text-[#04070c] rounded-lg px-4 py-2">{sending ? <Loader2 size={14} className="animate-spin" /> : <Send size={14} />} Send</button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
}
